import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const PLAID_ENVS = [
  { value: '', label: 'Use server default' },
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'development', label: 'Development' },
  { value: 'production', label: 'Production' },
];

const PlaidIntegrationPanel = ({ isAdmin, userRole }) => {
  const navigate = useNavigate();

  const [env, setEnv] = useState('');
  const [persistedEnv, setPersistedEnv] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [persistedWebhook, setPersistedWebhook] = useState('');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [clientIdPresent, setClientIdPresent] = useState(false);
  const [secretPresent, setSecretPresent] = useState(false);

  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const load = async () => {
      try {
        const res = await axios.get(`${apiUrl}/settings/integrations`, {
          headers: { 'x-auth-token': token },
        });
        const p = res.data?.plaid || {};
        setEnv(p.env || '');
        setPersistedEnv(p.env || '');
        setWebhookUrl(p.webhook_url || '');
        setPersistedWebhook(p.webhook_url || '');
        setClientIdPresent(Boolean(p.client_id_present));
        setSecretPresent(Boolean(p.secret_present));
      } catch {
        setError('Failed to load Plaid settings.');
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setError('');
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please sign in to save settings.');
      return;
    }
    const body = {};
    if (env !== persistedEnv) body.env = env;
    if (webhookUrl.trim() !== persistedWebhook) body.webhook_url = webhookUrl.trim();
    if (clientId) body.client_id = clientId;
    if (secret) body.secret = secret;

    if (Object.keys(body).length === 0) {
      setError('Nothing to save.');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.patch(`${apiUrl}/settings/integrations/plaid`, body, {
        headers: { 'x-auth-token': token },
      });
      const p = res.data?.plaid || {};
      setPersistedEnv(p.env || '');
      setEnv(p.env || '');
      setPersistedWebhook(p.webhook_url || '');
      setWebhookUrl(p.webhook_url || '');
      setClientIdPresent(Boolean(p.client_id_present));
      setSecretPresent(Boolean(p.secret_present));
      setClientId('');
      setSecret('');
      setSavedAt(Date.now());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save Plaid settings.');
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    env !== persistedEnv ||
    webhookUrl.trim() !== persistedWebhook ||
    Boolean(clientId) ||
    Boolean(secret);

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Plaid</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Bank linking and transaction sync. Manage connected accounts in the Plaid manager.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/bank-connections')}
            className="px-3 py-1.5 bg-gray-800 text-white text-xs font-medium rounded-lg hover:bg-gray-900"
          >
            Open Plaid manager
          </button>
        </div>
      </div>
      <div className="p-5 space-y-5">
        <div>
          <label htmlFor="plaid-env" className="block text-sm font-medium text-gray-700 mb-1">
            Environment
          </label>
          <select
            id="plaid-env"
            value={env}
            onChange={(e) => setEnv(e.target.value)}
            disabled={!isAdmin || saving}
            className="w-full md:w-80 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          >
            {PLAID_ENVS.map((o) => (
              <option key={o.value || 'default'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            When unset, the server uses the <code className="text-xs bg-gray-100 px-1 rounded">PLAID_ENV</code>{' '}
            environment variable.
          </p>
        </div>

        <div>
          <label htmlFor="plaid-webhook" className="block text-sm font-medium text-gray-700 mb-1">
            Webhook URL
          </label>
          <input
            id="plaid-webhook"
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://app.example.com/api/plaid/webhook"
            disabled={!isAdmin || saving}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="plaid-client-id" className="block text-sm font-medium text-gray-700 mb-1">
              Client ID{' '}
              <span
                className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                  clientIdPresent ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {clientIdPresent ? 'set' : 'not set'}
              </span>
            </label>
            <input
              id="plaid-client-id"
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={clientIdPresent ? 'Leave blank to keep existing' : 'Paste Plaid client_id'}
              disabled={!isAdmin || saving}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
          <div>
            <label htmlFor="plaid-secret" className="block text-sm font-medium text-gray-700 mb-1">
              Secret{' '}
              <span
                className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                  secretPresent ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {secretPresent ? 'set' : 'not set'}
              </span>
            </label>
            <input
              id="plaid-secret"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={secretPresent ? 'Leave blank to keep existing' : 'Paste Plaid secret'}
              disabled={!isAdmin || saving}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              autoComplete="new-password"
              spellCheck="false"
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Secrets are stored server-side and never returned by the API; you will only see whether each value is set.
        </p>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}

        {savedAt > 0 && !error && !dirty && (
          <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            Saved.
          </p>
        )}

        {isAdmin ? (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty || !loaded}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save Plaid settings'}
          </button>
        ) : (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Only <strong>admin</strong> users can change these settings. Your role is{' '}
            <strong>{userRole || 'unknown'}</strong>.
          </p>
        )}
      </div>
    </section>
  );
};

export default PlaidIntegrationPanel;
